// The rebinding screen's model interface and its panel (decision 0063: "a full rebinding screen in spike 002, on the
// action maps"). The model is the input layer's (the runtime's action maps); the panel only lists, captures, sets,
// resets and shows conflicts through this small interface, so any input model that implements it gets the screen.
import { h, type VNode } from "preact";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type El = VNode<any>;
import { Button, Icon, Status, TabPanel, Tabs } from "./components";
import type { ScreenContext, Ui } from "./app";

/** A device family a binding belongs to. The game decides which it offers. */
export type DeviceFamily = string;

/** One bindable slot of an action on a device (a button action has one or two; a stick has four directions). */
export interface RebindSlot {
  id: string;
  /** A string id naming the slot ("Up", "Alternate"), or none for a single slot. */
  label?: string;
  /** The control bound (the input layer's name, e.g. "KeyW", "Pad0"), or null when unbound. */
  control: string | null;
  /** Shown but not rebindable (a fixed touch control, the pointer). */
  fixed?: boolean;
}

/** Two or more bindings sharing one control on one device, in a context where both are live. */
export interface RebindConflict {
  device: DeviceFamily;
  control: string;
  bindings: { action: string; slot: string }[];
}

/** What the rebinding screen needs from the input layer. */
export interface RebindModel {
  /** The device families, in tab order. */
  devices(): readonly DeviceFamily[];
  /** The actions, in list order; `label` is a string id. */
  actions(): readonly { id: string; label: string }[];
  /** An action's slots on a device (empty when the action has none there). */
  bindings(action: string, device: DeviceFamily): readonly RebindSlot[];
  /**
   * Wait for the next control pressed on a device; `done(control)` gets it, or null when the player cancelled
   * (Escape, or the pad's back). Returns a function that stops waiting.
   */
  capture(device: DeviceFamily, done: (control: string | null) => void): () => void;
  /** Bind a control to a slot; returns the conflicts it leaves on that device. */
  set(action: string, device: DeviceFamily, slot: string, control: string): readonly RebindConflict[];
  /** Back to the defaults: one action on one device, one device, or everything. */
  reset(action?: string, device?: DeviceFamily): void;
  /** Every conflict now (on one device, or all). */
  conflicts(device?: DeviceFamily): readonly RebindConflict[];
}

/** String ids the panel's own text uses (the game's strings table holds the text). */
export interface RebindLabels {
  /** The tab list's name. */
  devices: string;
  /** Per device family: its tab label. */
  device: Record<DeviceFamily, string>;
  /** The slot button's accessible name (params action, slot: the slot's label or "none", control). */
  slotName: string;
  /** Shown while waiting for a press (param action). */
  waiting: string;
  /** The slot button's text while it waits for a press. */
  capture: string;
  /** Shown after a capture is cancelled. */
  cancelled: string;
  /** A control nobody has bound. */
  unbound: string;
  /** "Also bound to {actions}" (param actions). */
  conflict: string;
  /** The reset-all button. */
  resetAll: string;
  /** Announced after a set (params action, control). */
  bound: string;
}

/** The panel's state between renders (kept by the screen's params or the game). */
export interface RebindState {
  device: DeviceFamily;
  /** The slot waiting for a press: "action/slot", or null. */
  capturing: string | null;
  status: string;
}

/** The rebinding panel: a tab per device, a row per action with a button per slot, conflicts under the row. */
export function RebindPanel(p: {
  ctx: ScreenContext;
  model: RebindModel;
  labels: RebindLabels;
  state: RebindState;
  /** A control's name for the player (through the strings table). */
  controlLabel: (control: string, device: DeviceFamily) => string;
}): El {
  const { ctx, model, labels, state } = p;
  const t = ctx.t;
  const ui: Ui = ctx.ui;
  const device = state.device;
  const conflicts = model.conflicts(device);
  const actionLabel = (id: string): string => t(model.actions().find((a) => a.id === id)?.label ?? id);
  const conflictFor = (action: string, slot: string): string[] =>
    conflicts
      .filter((c) => c.bindings.some((b) => b.action === action && b.slot === slot))
      .flatMap((c) => c.bindings.filter((b) => !(b.action === action && b.slot === slot)).map((b) => actionLabel(b.action)));

  const startCapture = (action: string, slot: string): void => {
    state.capturing = `${action}/${slot}`;
    state.status = t(labels.waiting, { action: actionLabel(action) });
    ui.holdInput(true);
    ui.refresh();
    model.capture(device, (control) => {
      ui.holdInput(false);
      state.capturing = null;
      if (control === null) state.status = t(labels.cancelled);
      else {
        model.set(action, device, slot, control);
        state.status = t(labels.bound, { action: actionLabel(action), control: p.controlLabel(control, device) });
      }
      ui.refresh();
      // keep the focus on the slot just changed
      ctx.ui.root.querySelector<HTMLElement>(`[data-slot="${action}/${slot}"]`)?.focus();
    });
  };

  const rows = model
    .actions()
    .map((a) => {
      const slots = model.bindings(a.id, device);
      if (!slots.length) return null;
      const warn = slots.flatMap((s) => conflictFor(a.id, s.id));
      const rowLabelId = `rebind-${a.id}-label`;
      return h(
        "div",
        { class: "rpg-row", "data-action-row": a.id },
        h("span", { class: "rpg-label", id: rowLabelId }, t(a.label)),
        h(
          "div",
          { class: "rpg-options", role: "group", "aria-labelledby": rowLabelId, "data-nav-row": "" },
          slots.map((s) => {
            const control = s.control ? p.controlLabel(s.control, device) : t(labels.unbound);
            const waiting = state.capturing === `${a.id}/${s.id}`;
            return h(
              "button",
              {
                type: "button",
                class: "rpg-btn",
                "data-slot": `${a.id}/${s.id}`,
                disabled: s.fixed,
                "aria-label": t(labels.slotName, { action: t(a.label), slot: s.label ? t(s.label) : "none", control }),
                "aria-pressed": waiting ? "true" : undefined,
                onClick: () => !s.fixed && !state.capturing && startCapture(a.id, s.id),
              },
              waiting ? t(labels.capture) : control,
            );
          }),
        ),
        warn.length ? h("p", { class: "rpg-warn" }, h("span", { style: "display:inline-block;width:1em;height:1em;vertical-align:-0.15em;margin-right:0.3em" }, h(Icon, { name: "warn" })), t(labels.conflict, { actions: [...new Set(warn)].join(", ") })) : null,
      );
    })
    .filter(Boolean);

  return h(
    "div",
    { "data-rebind": device },
    h(Tabs, {
      id: "rebind",
      label: t(labels.devices),
      tabs: model.devices().map((d) => ({ id: d, label: t(labels.device[d] ?? d) })),
      selected: device,
      onSelect: (d) => {
        state.device = d;
        state.status = "";
        ui.refresh();
      },
    }),
    h(TabPanel, { id: "rebind", selected: device }, rows),
    h(Status, { text: state.status }),
    h(Button, {
      label: t(labels.resetAll),
      action: "rebind-reset",
      onPress: () => {
        model.reset(undefined, device);
        state.status = "";
        ui.refresh();
      },
    }),
  );
}
